import React, {useState, useEffect} from 'react'
import { authFetch } from '../api/authFetch'
import ChatSlotsPanel from '../components/ChatSlotsPanel'
import InlineAlert from '../components/InlineAlert'

function EnvelopeIcon({className}) {
    return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
    )
}

export default function Chats() {
    const [chats, setChats] = useState([])
    const [loading, setLoading] = useState(true)
    const [page, setPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [search, setSearch] = useState('')
    const [expandedChatId, setExpandedChatId] = useState(null)
    const [messageChat, setMessageChat] = useState(null)
    const [messageText, setMessageText] = useState('')
    const [sendLoading, setSendLoading] = useState(false)
    const [sendError, setSendError] = useState(null)
    const [sendSuccessDescription, setSendSuccessDescription] = useState(null)

    useEffect(() => {
        fetchChats()
    }, [page])

    const fetchChats = async () => {
        setLoading(true)
        try {
            const params = new URLSearchParams({page, limit: 20})
            if (search) params.append('search', search)

            const res = await authFetch(`/api/chats?${params}`)
            const data = await res.json()
            setChats(data.chats || [])
            setTotalPages(data.totalPages || 1)
        } catch (err) {
            console.error('Failed to fetch chats:', err)
        } finally {
            setLoading(false)
        }
    }

    const handleSearch = (e) => {
        e.preventDefault()
        setPage(1)
        fetchChats()
    }

    const toggleActive = async (chat) => {
        try {
            await authFetch(`/api/chats/${chat.id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({isActive: !chat.isActive})
            })
            fetchChats()
        } catch (err) {
            console.error('Failed to update chat:', err)
        }
    }

    const formatChatSummary = (chat) => {
        const parts = [`chat ID ${chat.chatId}`]
        if (chat.username) parts.push(`@${chat.username}`)
        const name = [chat.firstName, chat.lastName].filter(Boolean).join(' ')
        if (name) parts.push(name)
        return parts.join(', ')
    }

    const openMessageModal = (chat) => {
        setMessageChat(chat)
        setMessageText('')
        setSendError(null)
    }

    const closeMessageModal = () => {
        setMessageChat(null)
        setMessageText('')
        setSendError(null)
        setSendLoading(false)
    }

    const handleSendMessage = async () => {
        if (!messageChat) return
        const trimmed = messageText.trim()
        if (!trimmed) {
            setSendError('Enter a message')
            return
        }
        setSendLoading(true)
        setSendError(null)
        try {
            const res = await authFetch(`/api/chats/${messageChat.id}/send-message`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({text: trimmed})
            })
            const data = await res.json().catch(() => ({}))
            if (!res.ok) {
                setSendError(data.error || 'Failed to send')
                return
            }
            setSendSuccessDescription(formatChatSummary(messageChat))
            closeMessageModal()
        } catch (err) {
            console.error('Failed to send message:', err)
            setSendError('Failed to send')
        } finally {
            setSendLoading(false)
        }
    }

    return (
        <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-6">Chats</h1>

            {sendSuccessDescription && (
                <InlineAlert
                    variant="success"
                    onDismiss={() => setSendSuccessDescription(null)}
                >
                    <p>
                        Message was sent successfully to {sendSuccessDescription}.
                    </p>
                </InlineAlert>
            )}

            {/* Search */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
                <form onSubmit={handleSearch} className="flex gap-4">
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by username, name or chat ID..."
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                    <button type="submit"
                            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition">
                        Search
                    </button>
                </form>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="w-14 px-4 py-3" aria-hidden="true" />
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                    Slots
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Chat ID</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Active</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                            {chats.map((chat) => (
                                <React.Fragment key={chat.id}>
                                    <tr key={chat.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-4 text-center align-middle">
                                            {chat.isActive ? (
                                                <button
                                                    type="button"
                                                    onClick={() => openMessageModal(chat)}
                                                    className="inline-flex text-indigo-600 hover:text-indigo-800 p-1 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
                                                    title="Send Telegram message"
                                                    aria-label="Send Telegram message"
                                                >
                                                    <EnvelopeIcon className="w-5 h-5" />
                                                </button>
                                            ) : (
                                                <span className="inline-block w-7 h-7" aria-hidden="true" />
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="inline-flex items-center justify-end gap-2">
                                                <span className="text-sm tabular-nums text-grey-700">
                                                    {chat.activeSlots ?? 0}/{chat.inactiveSlots ?? 0}/{chat.removedSlots ?? 0}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setExpandedChatId(expandedChatId === chat.id ? null : chat.id)
                                                    }
                                                    className="cursor-pointer text-indigo-500 hover:text-indigo-700 p-0.5 leading-none"
                                                    title={expandedChatId === chat.id ? 'Collapse' : 'Expand slots'}
                                                    aria-expanded={expandedChatId === chat.id}
                                                >
                                                    {expandedChatId === chat.id ? '▲' : '▼'}
                                                </button>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500">
                                            {new Date(chat.createdAt).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-900">{chat.chatId}</td>
                                        <td className="px-6 py-4 text-sm text-gray-500">@{chat.username || '-'}</td>
                                        <td className="px-6 py-4 text-sm text-gray-500">
                                            {[chat.firstName, chat.lastName].filter(Boolean).join(' ') || '-'}
                                        </td>
                                        <td className="px-6 py-4">
                                            <button
                                                onClick={() => toggleActive(chat)}
                                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                                                    chat.isActive ? 'bg-green-500' : 'bg-gray-300'
                                                }`}
                                            >
                                                <span
                                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                                                        chat.isActive ? 'translate-x-6' : 'translate-x-1'
                                                    }`}
                                                />
                                            </button>
                                        </td>
                                    </tr>
                                    {expandedChatId === chat.id && (
                                        <tr className="bg-gray-50">
                                            <td colSpan={7} className="px-6 py-4 text-sm text-gray-700">
                                                <div className="max-w-5xl space-y-2 transition-all duration-200 ease-in-out">
                                                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                                        Slots & notification counts
                                                    </p>
                                                    <ChatSlotsPanel chatRowId={chat.id} />
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                    <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                            Previous
                        </button>
                        <button
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>

            {messageChat && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
                    role="presentation"
                    onClick={(e) => e.target === e.currentTarget && !sendLoading && closeMessageModal()}
                >
                    <div
                        className="bg-white rounded-xl shadow-xl border border-gray-200 max-w-lg w-full p-6"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="send-message-title"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2 id="send-message-title" className="text-lg font-semibold text-gray-900 mb-1">
                            Send to Telegram
                        </h2>
                        <p className="text-sm text-gray-500 mb-4">
                            Chat ID {messageChat.chatId}
                            {messageChat.username ? ` · @${messageChat.username}` : ''}
                        </p>
                        <textarea
                            value={messageText}
                            onChange={(e) => setMessageText(e.target.value)}
                            rows={6}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y"
                            placeholder="Message text..."
                            disabled={sendLoading}
                        />
                        {sendError && (
                            <InlineAlert
                                variant="error"
                                className="mt-3 mb-0 px-3 py-2 text-xs"
                                onDismiss={() => setSendError(null)}
                            >
                                <p>{sendError}</p>
                            </InlineAlert>
                        )}
                        <div className="mt-4 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => !sendLoading && closeMessageModal()}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSendMessage}
                                disabled={sendLoading}
                                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50"
                            >
                                {sendLoading ? 'Sending…' : 'Send'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
